/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {GenericObject} from '@ion/core/src/types/entities';
import {hasValue, isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';

/**
 * @hook useHasChange
 *
 * Provides a utility hook for determining if an object has a change i.e if any of its
 * property has a value. If it has, returns true else returns false.
 *
 *
 * ```typescript
 * import {useHasChange} from '@ion/dls/src/hooks/';
 *
 * ...in component,
 *
 * const hasChange = useHasChange(target);
 *
 * ```
 */
export function useHasChange(target: GenericObject = {}, ignoredTargets?: Array<string | number>) {
  const [hasChange, setHasChange] = useState<boolean>(false);
  useEffect(() => {
    if (!isObjectEmpty(target)) {
      const final = {...target};
      ignoredTargets?.forEach?.(ignored => {
        !!final[ignored] && delete final[ignored];
      });
      const isEdited = hasValue(final);
      setHasChange(isEdited);
    }
  }, [target]);
  return hasChange;
}

export default useHasChange;
